/**
 * The Quoted Book - every live QUOTED deal scored with the
 * transparent likelihood model (client history + POC + quote size +
 * BD priority + methodology + age decay + engagement priors), ranked
 * by expected value. Hover the likelihood pill for the factor trail.
 *
 * Insight charts (band donut, age-vs-win-window, team pie, top
 * clients by expected £, quoting momentum) sit between the KPI strip
 * and the table. Team/client drill-down cross-filters everything and
 * lives in the URL (?team=…&client=…&book=quoted) for shareability.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, AlertTriangle, Eye, Download, SlidersHorizontal,
  Users, FileDiff, HelpCircle, LinkIcon, Search, ArrowDown, ArrowUp, X,
} from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../ui/tooltip";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { TeamPie, TopClients, MonthlyTrend } from "./CommissionedPanels";
import { BandDonut, AgeHistogram } from "./QuotedInsights";
import { clientKey, buildClientIndex } from "./clientGrouping";
import ContestReview from "./ContestReview";
import { EngagementBacktest } from "./EngagementBacktest";

const API = process.env.REACT_APP_BACKEND_URL;

const BAND_META = {
  hot:  { label: "Hot",  pill: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  warm: { label: "Warm", pill: "bg-sky-100 text-sky-700 border-sky-200" },
  cool: { label: "Cool", pill: "bg-amber-100 text-amber-700 border-amber-200" },
  cold: { label: "Cold", pill: "bg-stone-100 text-stone-500 border-stone-200" },
};

const MONTH_OPTIONS = [
  { value: 3, label: "Last 3 months" },
  { value: 6, label: "Last 6 months" },
  { value: 12, label: "Last 12 months" },
  { value: 0, label: "All time" },
];

const fmtGbp = (v) => (v == null || Number.isNaN(v) ? "-" : `£${Math.round(v).toLocaleString("en-GB")}`);
const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
};
const fmtGbpShort = (v) => {
  if (v == null) return "-";
  if (Math.abs(v) >= 1_000_000) return `£${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `£${Math.round(v / 1_000).toLocaleString("en-GB")}k`;
  return `£${Math.round(v).toLocaleString("en-GB")}`;
};

const last12MonthKeys = () => {
  const keys = [];
  const now = new Date();
  let y = now.getFullYear();
  let m = now.getMonth();
  for (let i = 0; i < 12; i++) {
    keys.unshift(`${y}-${String(m + 1).padStart(2, "0")}`);
    m -= 1;
    if (m < 0) { m = 11; y -= 1; }
  }
  return keys;
};

const groupAgg = (items, keyFn, valFn) => {
  const g = new Map();
  for (const i of items) {
    const k = keyFn(i);
    const b = g.get(k) || { count: 0, value: 0 };
    b.count += 1;
    b.value += valFn(i) || 0;
    g.set(k, b);
  }
  return [...g.entries()].map(([k, v]) => ({ key: k, ...v }));
};

function SortableTh({ label, k, sortKey, sortDir, onSort, align, testid }) {
  const active = sortKey === k;
  const Arrow = sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-3 py-2.5 font-medium text-${align}`}>
      <button
        type="button"
        onClick={() => onSort(k)}
        data-testid={testid}
        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors ${active ? "text-[#1B4F8A] font-semibold" : "text-stone-400 hover:text-stone-600"}`}
      >
        {label}
        <Arrow className={`w-3 h-3 ${active ? "opacity-100" : "opacity-30"}`} />
      </button>
    </th>
  );
}

function FilterChip({ label, onClear, testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-900 bg-sky-50 border border-sky-200 rounded-full pl-3 pr-1.5 py-1"
    >
      {label}
      <button
        type="button"
        onClick={onClear}
        data-testid={`${testid}-clear`}
        className="rounded-full p-0.5 hover:bg-sky-100 transition-colors"
        title="Remove filter"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

function EngagementChips({ eng }) {
  if (!eng?.has_link) {
    return <span className="text-[10px] font-mono text-stone-300">no link</span>;
  }
  const chips = [
    { icon: Eye, v: eng.sessions || 0, title: "client visits" },
    { icon: Users, v: eng.people || 0, title: "stakeholders" },
    { icon: Download, v: eng.downloads || 0, title: "Excel downloads" },
    { icon: SlidersHorizontal, v: eng.sample_changes || 0, title: "sample edits" },
    { icon: FileDiff, v: eng.revisions || 0, title: "revisions" },
  ];
  return (
    <span className="inline-flex items-center gap-2">
      <LinkIcon className="w-3 h-3 text-[#3EA8DC]" title="share link minted" />
      {chips.map(({ icon: Icon, v, title }, i) => (
        <span
          key={i}
          title={title}
          className={`inline-flex items-center gap-0.5 text-[11px] font-mono ${v > 0 ? "text-slate-700" : "text-stone-300"}`}
        >
          <Icon className="w-3 h-3" />{v}
        </span>
      ))}
    </span>
  );
}

function FactorTooltip({ item, children }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="left"
        className="max-w-sm bg-slate-900 text-stone-100 text-xs leading-relaxed px-3 py-2.5"
        data-testid={`quoted-book-factors-${item.cid}`}
      >
        <p className="font-semibold text-white mb-1.5">
          Why {Math.round(item.probability * 100)}%?
        </p>
        <ul className="space-y-1">
          {(item.factors || []).map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`font-mono shrink-0 ${f.multiplier > 1 ? "text-emerald-400" : f.multiplier < 1 ? "text-rose-400" : "text-stone-400"}`}>
                ×{Number(f.multiplier).toFixed(2)}
              </span>
              <span>{f.label}: {f.detail}</span>
            </li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}

function ExplainerDialog({ open, onClose, meta }) {
  const bt = meta?.backtest || {};
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="likelihood-explainer">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light text-slate-900">
            How likelihood is calculated
          </DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm text-stone-600 space-y-3 pt-2 text-left">
              <p>
                Every quote starts at the base rate -{" "}
                <span className="font-semibold text-slate-800">
                  {meta ? `${(meta.base_rate * 100).toFixed(0)}%` : "…"}
                </span>{" "}
                of the {meta?.cohort_n?.toLocaleString() ?? "…"} settled quotes since 2023 commissioned
                (recent years weighted higher; a quote still open 180 days after issue counts as lost) - then adjusts on evidence: the client's own win
                history, the named contact, quote size band, study type and methodology, plus how
                long the quote has been open. The age discount is specific to the study type: PRO
                quotes convert far slower than market research, so they decay more gently. Where a
                share link exists, engagement (opens, return visits, downloads, sample edits, extra viewers, revisions) moves the
                score by weights learned from portal outcomes. Post-win fields are excluded so nothing leaks the outcome.
              </p>
              {bt.available ? (
                <div>
                  <p className="font-semibold text-slate-800 mb-1.5">
                    Does it work? Backtest: trained on 2023-2025, scored 2026 blind
                    {bt.auc != null && <> · AUC {bt.auc}</>}
                  </p>
                  <table className="w-full text-xs border border-stone-200 rounded overflow-hidden">
                    <thead>
                      <tr className="bg-stone-50 text-stone-500 font-mono uppercase text-[10px]">
                        <th className="px-2 py-1.5 text-left">Score bucket</th>
                        <th className="px-2 py-1.5 text-right">Quotes</th>
                        <th className="px-2 py-1.5 text-right">Predicted</th>
                        <th className="px-2 py-1.5 text-right">Actually won</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(bt.quintiles || []).map((q) => (
                        <tr key={q.bucket} className="border-t border-stone-100">
                          <td className="px-2 py-1">{["Lowest", "Low", "Middle", "High", "Highest"][q.bucket - 1]}</td>
                          <td className="px-2 py-1 text-right font-mono">{q.n}</td>
                          <td className="px-2 py-1 text-right font-mono">{(q.predicted * 100).toFixed(0)}%</td>
                          <td className="px-2 py-1 text-right font-mono font-semibold">{(q.actual * 100).toFixed(0)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-stone-400 italic">Backtest not yet available.</p>
              )}
              <EngagementBacktest data={meta?.engagement_backtest} />
              <p className="text-[11px] text-stone-400">
                Scores are a prioritisation tool, not a promise - use them to decide
                what to chase first. The model retrains itself on every SharePoint sync.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

export default function QuotedBook() {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [months, setMonths] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showExplainer, setShowExplainer] = useState(false);
  const [query, setQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortKey, setSortKey] = useState("expected_value");
  const [sortDir, setSortDir] = useState("desc");
  const [limit, setLimit] = useState(100);
  const [medianWin, setMedianWin] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const teamFilter = searchParams.get("team");
  const clientFilter = searchParams.get("client");

  useEffect(() => { setLimit(100); }, [months, query, dateFrom, dateTo, teamFilter, clientFilter]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value == null) next.delete(key);
    else next.set(key, value);
    if (next.get("team") || next.get("client")) next.set("book", "quoted");
    else next.delete("book");
    setSearchParams(next, { replace: true });
  };
  const toggleTeam = (t) => setFilter("team", teamFilter === t ? null : t);
  const toggleClient = (c) => setFilter("client", clientFilter === c ? null : c);
  const clearAll = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("team");
    next.delete("client");
    next.delete("book");
    setSearchParams(next, { replace: true });
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(
        `${API}/api/proposals/pipeline/quoted-likelihood?months=${m}`
      );
      setData(d);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load the quoted book.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(months); }, [load, months]);
  useEffect(() => {
    axios.get(`${API}/api/proposals/pipeline/likelihood-model`)
      .then((r) => setMeta(r.data)).catch(() => {});
    axios.get(`${API}/api/proposals/pipeline/commissioned-book`)
      .then((r) => setMedianWin(r.data?.totals?.median_days_to_win ?? null))
      .catch(() => {});
  }, []);

  // Search / date filters (global scope for both charts and table).
  const baseItems = useMemo(() => {
    let all = data?.items || [];
    const q = query.trim().toLowerCase();
    if (q) {
      all = all.filter((i) => {
        const hay = [
          i.client_company, i.topic, i.project_name, i.client_poc,
          ...(i.cids || [i.cid]).map((c) => `#${c} ${c}`),
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      });
    }
    if (dateFrom) all = all.filter((i) => (i.proposal_date || "").slice(0, 10) >= dateFrom);
    if (dateTo) all = all.filter((i) => (i.proposal_date || "").slice(0, 10) <= dateTo);
    return all;
  }, [data, query, dateFrom, dateTo]);

  // Cross-filter scoping: each drillable panel excludes its OWN
  // dimension's filter but applies the other (same as Commissioned).
  const clientIndex = useMemo(() => buildClientIndex(data?.items || []), [data]);

  const view = useMemo(() => {
    const clientScoped = clientFilter ? baseItems.filter((i) => clientKey(i) === clientFilter) : baseItems;
    const teamScoped = teamFilter ? baseItems.filter((i) => i.team === teamFilter) : baseItems;
    const filtered = teamFilter ? clientScoped.filter((i) => i.team === teamFilter) : clientScoped;

    const byTeam = groupAgg(clientScoped, (i) => i.team || "Unspecified", (i) => i.value_gbp)
      .map((g) => ({ team: g.key, count: g.count, value: g.value }))
      .sort((a, b) => b.count - a.count);

    let topClients = groupAgg(teamScoped, clientKey, (i) => i.expected_value)
      .map((g) => ({
        key: g.key,
        company: clientIndex.get(g.key)?.label || g.key,
        variants: clientIndex.get(g.key)?.variants || [],
        count: g.count,
        value: g.value,
      }))
      .sort((a, b) => b.value - a.value);
    const head = topClients.slice(0, 8);
    if (clientFilter && !head.some((c) => c.key === clientFilter)) {
      const sel = topClients.find((c) => c.key === clientFilter);
      if (sel) head.push(sel);
    }
    topClients = head;

    const monthKeys = last12MonthKeys();
    const mg = Object.fromEntries(monthKeys.map((k) => [k, { count: 0, value: 0 }]));
    for (const i of filtered) {
      const k = (i.proposal_date || "").slice(0, 7);
      if (mg[k]) { mg[k].count += 1; mg[k].value += i.value_gbp || 0; }
    }
    const monthly = monthKeys.map((k) => ({ month: k, ...mg[k] }));

    const totals = {
      count: filtered.length,
      book_value: filtered.reduce((s, i) => s + (i.value_gbp || 0), 0),
      expected_value: filtered.reduce((s, i) => s + (i.expected_value || 0), 0),
      hot: filtered.filter((i) => i.band === "hot").length,
    };

    return { filtered, byTeam, topClients, monthly, totals };
  }, [baseItems, teamFilter, clientFilter, clientIndex]);

  const items = useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1;
    return [...view.filtered].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      const cmp = typeof va === "string" || typeof vb === "string"
        ? String(va || "").localeCompare(String(vb || ""))
        : (va ?? 0) - (vb ?? 0);
      return dir * cmp || (b.probability - a.probability);
    });
  }, [view.filtered, sortKey, sortDir]);

  const t = view.totals;
  const hasFilter = Boolean(teamFilter || clientFilter);

  return (
    <section
      id="quoted-book"
      data-testid="quoted-book"
      className="bg-white border border-stone-200 rounded-2xl overflow-hidden shadow-sm mt-6 scroll-mt-20"
    >
      <header className="flex flex-wrap items-center gap-3 px-5 pt-4 pb-3 border-b border-stone-100">
        <div className="mr-auto">
          <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A] mb-0.5">
            The quoted book
          </p>
          <p className="text-sm text-stone-600">
            Live opportunities ranked by <span className="font-medium text-slate-800">expected commission value</span> (likelihood × quote value). Sibling options are grouped - only one can commission. Click a team or client to drill down.
          </p>
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-stone-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search # / client / indication / name…"
            data-testid="quoted-book-search"
            className="text-xs border border-stone-300 rounded-md pl-8 pr-2 py-1.5 w-64 text-slate-700 bg-white placeholder:text-stone-400 focus:outline-none focus:border-[#3EA8DC]"
          />
        </div>
        <div className="flex items-center gap-1.5 text-xs text-stone-500">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="quoted-book-date-from"
            title="Quoted from"
            className="text-xs border border-stone-300 rounded-md px-2 py-1 text-slate-700 bg-white"
          />
          <span>-</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="quoted-book-date-to"
            title="Quoted until"
            className="text-xs border border-stone-300 rounded-md px-2 py-1 text-slate-700 bg-white"
          />
        </div>
        <select
          value={months}
          onChange={(e) => setMonths(Number(e.target.value))}
          data-testid="quoted-book-months"
          className="text-xs border border-stone-300 rounded-md px-2 py-1.5 text-slate-700 bg-white"
        >
          {MONTH_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setShowExplainer(true)}
          data-testid="quoted-book-explainer-btn"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B4F8A] hover:text-[#3EA8DC] transition-colors"
        >
          <HelpCircle className="w-3.5 h-3.5" />
          how scores work
        </button>
      </header>

      <ContestReview onChanged={() => load(months)} />

      {loading && (
        <div className="flex items-center gap-2 text-stone-500 text-sm px-5 py-8" data-testid="quoted-book-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          Scoring the quoted book…
        </div>
      )}
      {!loading && error && (
        <div className="mx-5 my-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2" data-testid="quoted-book-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {hasFilter && (
            <div
              data-testid="quoted-filter-bar"
              className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-sky-100 bg-sky-50/40"
            >
              <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500">
                Drilled into
              </span>
              {teamFilter && (
                <FilterChip
                  label={`Team: ${teamFilter}`}
                  onClear={() => setFilter("team", null)}
                  testid="quoted-filter-chip-team"
                />
              )}
              {clientFilter && (
                <FilterChip
                  label={`Client: ${clientIndex.get(clientFilter)?.label || clientFilter}`}
                  onClear={() => setFilter("client", null)}
                  testid="quoted-filter-chip-client"
                />
              )}
              <button
                type="button"
                onClick={clearAll}
                data-testid="quoted-filter-clear"
                className="text-xs font-semibold text-sky-800 hover:text-sky-600 transition-colors ml-1"
              >
                clear all
              </button>
            </div>
          )}

          {/* totals strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-stone-100">
            {[
              { label: "Live opportunities", value: t.count?.toLocaleString() ?? "-", testid: "quoted-book-kpi-count" },
              { label: "Book value", value: fmtGbpShort(t.book_value), testid: "quoted-book-kpi-book" },
              { label: "Expected commission", value: fmtGbpShort(t.expected_value), tone: "text-[#1B4F8A]", testid: "quoted-book-kpi-expected" },
              { label: "Hot quotes", value: t.hot?.toLocaleString() ?? "-", tone: "text-emerald-700", testid: "quoted-book-kpi-hot" },
            ].map((k) => (
              <div key={k.label} className="bg-white px-4 py-3" data-testid={k.testid}>
                <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mb-1">{k.label}</p>
                <p className={`text-2xl font-medium tabular-nums leading-none ${k.tone || "text-slate-900"}`}>{k.value}</p>
              </div>
            ))}
          </div>

          {/* insight charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 px-5 py-4 border-t border-stone-100 bg-stone-50/50">
            <BandDonut items={view.filtered} />
            <AgeHistogram items={view.filtered} medianWinDays={medianWin} />
            <TeamPie byTeam={view.byTeam} selected={teamFilter} onSelect={toggleTeam} idPrefix="quoted" />
            <TopClients
              clients={view.topClients}
              selected={clientFilter}
              onSelect={toggleClient}
              title="Top clients by expected commission"
              idPrefix="quoted"
            />
            <div className="md:col-span-2 xl:col-span-2">
              <MonthlyTrend
                monthly={view.monthly}
                title="Quoted per month · last 12 months"
                color="#3EA8DC"
                idPrefix="quoted"
              />
            </div>
          </div>

          {/* table */}
          <div className="overflow-x-auto border-t border-stone-100">
            <TooltipProvider delayDuration={150}>
              <table className="w-full text-sm" data-testid="quoted-book-table">
                <thead>
                  <tr className="text-[10px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-100">
                    <th className="px-5 py-2.5 text-left font-medium">Client / project</th>
                    <SortableTh label="Value" k="value_gbp" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" testid="quoted-book-sort-value" />
                    <SortableTh label="Quoted" k="proposal_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" testid="quoted-book-sort-date" />
                    <SortableTh label="Likelihood" k="probability" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="center" testid="quoted-book-sort-likelihood" />
                    <SortableTh label="Expected £" k="expected_value" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" testid="quoted-book-sort-expected" />
                    <th className="px-5 py-2.5 text-left font-medium">Engagement</th>
                  </tr>
                </thead>
                <tbody>
                  {items.slice(0, limit).map((item) => {
                    const band = BAND_META[item.band] || BAND_META.cold;
                    const multi = (item.option_count || 1) > 1;
                    return (
                      <tr
                        key={(item.cids || [item.cid]).join("-")}
                        data-testid={`quoted-book-row-${item.cid}`}
                        className={`border-b border-stone-50 hover:bg-stone-50/60 transition-colors ${item.dead_weight ? "opacity-45" : ""}`}
                      >
                        <td className="px-5 py-2.5">
                          <p className="font-medium text-slate-900 leading-snug truncate max-w-[260px]">
                            <button
                              type="button"
                              onClick={() => toggleClient(clientKey(item))}
                              data-testid={`quoted-row-client-${item.cid}`}
                              title={`Drill into ${item.client_company || "Unknown"}`}
                              className="hover:text-[#1B4F8A] hover:underline transition-colors text-left"
                            >
                              {item.client_company || "Unknown client"}
                            </button>
                            {multi && (
                              <span
                                title={`Option CIDs: ${(item.cids || []).join(", ")} - only one can commission`}
                                data-testid={`quoted-book-options-${item.cid}`}
                                className="ml-2 text-[9px] font-mono uppercase text-indigo-600 bg-indigo-50 border border-indigo-200 rounded px-1 py-px"
                              >
                                ×{item.option_count} options
                              </span>
                            )}
                            {item.contested && (item.contested.member_count || 1) > 1 && (
                              <span
                                title={`Same end project quoted via ${item.contested.member_count} clients (${[item.client_company, ...(item.contested.rivals || [])].filter(Boolean).join(", ")}) - counted once, probability of winning through any of them`}
                                data-testid={`quoted-book-contested-${item.cid}`}
                                className="ml-2 text-[9px] font-mono uppercase text-rose-700 bg-rose-50 border border-rose-200 rounded px-1 py-px"
                              >
                                contested ×{item.contested.member_count}
                              </span>
                            )}
                            {item.dead_weight && (
                              <span className="ml-2 text-[9px] font-mono uppercase text-stone-400 border border-stone-200 rounded px-1 py-px">dead weight</span>
                            )}
                          </p>
                          <p className="text-[11px] text-stone-500 truncate max-w-[260px]">
                            {item.topic || item.project_name || ""}
                          </p>
                          <p className="text-[10px] font-mono text-stone-400 mt-0.5" data-testid={`quoted-book-cids-${item.cid}`}>
                            {(item.options || (item.cids || [item.cid]).map((c) => ({ cid: c }))).map((o, i) => (
                              <span key={o.cid}>
                                {i > 0 && " · "}
                                {o.admin_url ? (
                                  <a
                                    href={o.admin_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="Open in Cognito (admin)"
                                    data-testid={`quoted-book-cid-link-${o.cid}`}
                                    className="hover:text-[#1B4F8A] hover:underline transition-colors"
                                  >
                                    #{o.cid}
                                  </a>
                                ) : (
                                  <span>#{o.cid}</span>
                                )}
                              </span>
                            ))}
                          </p>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <span className="font-mono tabular-nums text-slate-700">{fmtGbp(item.value_gbp)}</span>
                          {multi && (
                            <p
                              className="text-[9px] font-mono text-stone-400 mt-0.5"
                              title={`Only one option can commission - using the ${item.value_basis}`}
                            >
                              {fmtGbpShort(item.value_min)}-{fmtGbpShort(item.value_max)} · {item.value_basis}
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap">
                          <span className="font-mono tabular-nums text-slate-700 text-xs">
                            {fmtDate(item.proposal_date)}
                          </span>
                          <p className="text-[9px] font-mono text-stone-400 mt-0.5">
                            {item.age_days != null ? `${item.age_days}d ago` : ""}
                          </p>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <FactorTooltip item={item}>
                            <span
                              data-testid={`quoted-book-likelihood-${item.cid}`}
                              className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border cursor-help ${band.pill}`}
                            >
                              {band.label}
                              <span className="font-mono">{Math.round(item.probability * 100)}%</span>
                            </span>
                          </FactorTooltip>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold text-[#1B4F8A]">
                          {item.expected_value ? fmtGbp(item.expected_value) : "-"}
                        </td>
                        <td className="px-5 py-2.5">
                          <EngagementChips eng={item.engagement} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TooltipProvider>
            {items.length === 0 && (
              <p className="px-5 py-8 text-sm text-stone-400" data-testid="quoted-book-empty">
                {hasFilter
                  ? "No live quotes match the current drill-down."
                  : query ? `No live quotes match "${query}".` : "No live quoted deals in this window."}
              </p>
            )}
            {items.length > limit && (
              <div className="px-5 py-3 border-t border-stone-100 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setLimit((l) => l + 100)}
                  data-testid="quoted-book-show-more"
                  className="text-xs font-semibold text-[#1B4F8A] hover:text-[#3EA8DC] transition-colors"
                >
                  Show more
                </button>
                <span className="text-[11px] text-stone-400">
                  showing {Math.min(limit, items.length)} of {items.length}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      <ExplainerDialog open={showExplainer} onClose={() => setShowExplainer(false)} meta={meta} />
    </section>
  );
}
